import { baseApi } from '@/store/api/baseApi';
import { API_URL } from '@/config/app';
import type { RootState } from '@/store';
import type {
  BulkCollectionIdsPayload,
  BulkCollectionStatusPayload,
  Collection,
  CollectionListParams,
  CreateCollectionPayload,
  PaginatedCollections,
  ReorderPayload,
  UpdateCollectionPayload,
} from '@/types/collection';
import { createAccessToken } from '@/utils/agent';
import { getRtkErrorMessage } from '@/utils/apiResponse';
import { isApiResponse } from '@/types/api';

function buildCollectionListQuery(params: CollectionListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'sortOrder');
  searchParams.set('sortOrder', params.sortOrder ?? 'asc');

  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.status) searchParams.set('status', params.status);
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);

  return searchParams.toString();
}

export const collectionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCollections: builder.query<PaginatedCollections, CollectionListParams>({
      query: (params) => ({
        url: `/collections?${buildCollectionListQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildCollectionListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildCollectionListQuery(currentArg ?? {}) !== buildCollectionListQuery(previousArg ?? {}),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Collections' as const, id })),
              { type: 'Collections', id: 'LIST' },
            ]
          : [{ type: 'Collections', id: 'LIST' }],
    }),
    getCollection: builder.query<Collection, string>({
      query: (id) => `/collections/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Collections', id }],
    }),
    createCollection: builder.mutation<Collection, CreateCollectionPayload>({
      query: (body) => ({ url: '/collections/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Collections', id: 'LIST' }],
    }),
    updateCollection: builder.mutation<Collection, { id: string; body: UpdateCollectionPayload }>({
      query: ({ id, body }) => ({ url: `/collections/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Collections', id },
        { type: 'Collections', id: 'LIST' },
      ],
    }),
    deleteCollection: builder.mutation<void, string>({
      query: (id) => ({ url: `/collections/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Collections', id: 'LIST' }],
    }),
    reorderCollections: builder.mutation<Collection[], ReorderPayload>({
      query: (body) => ({ url: '/collections/reorder', method: 'POST', body }),
      invalidatesTags: [{ type: 'Collections', id: 'LIST' }],
    }),
    bulkUpdateCollectionStatus: builder.mutation<{ count: number }, BulkCollectionStatusPayload>({
      query: (body) => ({ url: '/collections/bulk-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'Collections', id: 'LIST' }],
    }),
    bulkDeleteCollections: builder.mutation<{ count: number }, BulkCollectionIdsPayload>({
      query: (body) => ({ url: '/collections/bulk-delete', method: 'POST', body }),
      invalidatesTags: [{ type: 'Collections', id: 'LIST' }],
    }),
    uploadCollectionImage: builder.mutation<{ image: string }, FormData>({
      async queryFn(formData, api) {
        const { accessToken } = (api.getState() as RootState).auth;

        try {
          const response = await fetch(`${API_URL}/collections/upload-image`, {
            method: 'POST',
            headers: {
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              'x-access-token': createAccessToken(),
            },
            body: formData,
          });

          const json: unknown = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: getRtkErrorMessage({ data: json }) ?? 'Image upload failed.',
              },
            };
          }

          const payload = isApiResponse<{ image: string }>(json) ? json.data : (json as { image: string });
          const message = isApiResponse<{ image: string }>(json) ? json.message : undefined;

          return {
            data: payload,
            ...(message ? { meta: { message } } : {}),
          };
        } catch {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: 'Image upload failed.',
            },
          };
        }
      },
    }),
  }),
});

export const {
  useGetCollectionsQuery,
  useLazyGetCollectionsQuery,
  useGetCollectionQuery,
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useDeleteCollectionMutation,
  useReorderCollectionsMutation,
  useBulkUpdateCollectionStatusMutation,
  useBulkDeleteCollectionsMutation,
  useUploadCollectionImageMutation,
} = collectionsApi;
